import { useEffect, useState, useContext } from 'react';
import Employee from '../../../services/Employee';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import DataTable from '../../Core/DataTableWithoutHeader';
import { Modal, Row, Col, FormGroup, Label, Input } from 'reactstrap';
import { createOptionsPage } from '../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../entities/PagedMeta';
import { PayRatesUpdateDescriptionForm, PayRatesListColumns, PayRatesForm } from './';
import ModalProfile from '../../Profile/ModalProfile';
import confirmAlertWithForm from '../../../helpers/confirmAlertWithForm';
import isUpperManagement from '../../../helpers/isUpperManagement';
import isFinanceUser from '../../../helpers/isFinanceUser';
import { isDirector, isAdmin } from '../../../helpers';
import { toast } from 'react-toastify';
import ApprovalChatModal from '../../../views/pages/Staff/DirectoryList/ApprovalChatModal';

const PayRatesList = ({ employeeId = null }: { employeeId?: string | null }) => {
  const { user, token, currentSchool }: any = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState<any>([]);
  const dataSelected = tableData.data?.find((d: any) => d.selected);
  const [filteredOptions, setFilteredOptions] = useState({
    filters: [],
    options: createOptionsPage(createPagedMeta(), { sortDesc: 'desc' }),
  });
  const [lastUpdatedAt, setLastUpdatedAt] = useState(new Date().getTime());
  const [modalUpdatePayment, setModalUpdatePayment] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPayRateId, setChatPayRateId] = useState('');
  const [chatTitle, setChatTitle] = useState('');

  const openPayRateChat = (id: string) => {
    const row = tableData.data?.find((d: any) => d.id === id);
    const name = row ? `${row?.employee?.user?.firstName || ''} ${row?.employee?.user?.lastName || ''}`.trim() : '';
    setChatPayRateId(id);
    setChatTitle(`Chat - Pay Raise: ${name}`);
    setChatOpen(true);
  };

  const setSelected = (id: string | null) => {
    const data = tableData.data?.map((d: any) => {
      return { ...d, ...{ selected: false } };
    });
    const selected = tableData.data?.find((d: any) => d.id === id);
    if (!selected) {
      PayRatesUpdateDescriptionForm;
      setTableData(JSON.parse(JSON.stringify({ meta: tableData.meta, data })));
      return false;
    }
    const index = tableData.data.indexOf(selected);
    data[index].selected = !selected.selected;
    setTableData(JSON.parse(JSON.stringify({ meta: tableData.meta, data })));
  };

  const handlePerRowsChange = async (perPage: number) => {
    const newOptions = {
      ...createPagedMeta(),
      perPage,
    };
    const newFilteredOptions = {
      filters: filteredOptions.filters,
      options: createOptionsPage(newOptions, { sortDesc: 'desc' }),
    };
    setFilteredOptions(newFilteredOptions);
  };

  const handlePageChange = async (page: number) => {
    const newOptions = {
      ...createPagedMeta(),
      currentPage: page,
    };
    const newFilteredOptions = {
      filters: filteredOptions.filters,
      options: createOptionsPage(newOptions, { sortDesc: 'desc' }),
    };
    setFilteredOptions(newFilteredOptions);
  };

  const viewAction = (_: any, id: string) => {
    setSelected(id);
    setModalUpdatePayment(true);
  };

  const handleApprove = async (e: React.FormEvent, id: string) => {
    e.preventDefault();
    const current = tableData.data.find((d: any) => d.id === id);
    return confirmAlertWithForm({
      title: 'Confirm',
      messageType: 'EDIT',
      message: 'Are you sure you want to accept this?',
      children: (
        <Row>
          <Col>
            <FormGroup className="form-control-label">
              <Label>Effective At</Label>
              <Input
                type="date"
                name="effectiveAt"
                defaultValue={
                  current?.effectiveAt
                    ? new Date(current.effectiveAt).toISOString().split('T')[0]
                    : new Date().toISOString().split('T')[0]
                }
              />
            </FormGroup>
          </Col>
        </Row>
      ),
      onSubmit: async (e: any) => {
        e.preventDefault();
        const data = new FormData(e.target as HTMLFormElement);
        const body = {} as any;
        for (const [key, value] of data.entries()) {
          (body as any)[key] = value;
        }
        await approveAction(body, id);
      },
    });
  };

  const approveAction = async (body: any, id: string) => {
    const current = tableData.data.find((d: any) => d.id === id);
    setLoading(true);
    try {
      await Employee(token).actionPayRates(current?.employeeId, id, 'approve', body);
      toast.success('Pay rate approved successfully');
      setLastUpdatedAt(new Date().getTime());
    } catch (e) {
      toast.error('Failed to approve pay rates');
      console.error('Failed to approve pay rates');
    } finally {
      setLoading(false);
    }
  };

  const denyAction = (e: any, id: string) => {
    const current = tableData.data.find((d: any) => d.id === id);
    return confirmAlertWithForm({
      title: 'Delete',
      messageType: 'DELETE',
      message: 'Are you sure to deny this pay-rate request?',
      children: (
        <Row>
          <Col>
            <FormGroup className="form-control-label">
              <Label>Reason</Label>
              <Input type="textarea" name="reason" />
            </FormGroup>
          </Col>
        </Row>
      ),
      onSubmit: async (e: any) => {
        e.preventDefault();
        setLoading(true);
        try {
          const data = new FormData(e.target as HTMLFormElement);
          const body = {} as any;
          for (const [key, value] of data.entries()) {
            (body as any)[key] = value;
          }
          await Employee(token).actionPayRates(current?.employeeId, id, 'deny', body);
          toast.success('Pay rate denied successfully');
          setLastUpdatedAt(new Date().getTime());
        } catch (e) {
          toast.error('Failed to deny pay rates');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = employeeId
          ? await Employee(token).listPayRates(employeeId, filteredOptions)
          : await Employee(token).listPayRatesBySchool(currentSchool.value, filteredOptions);
        setTableData({
          meta: response.meta,
          data: response.data.map((r: any) => {
            return { ...{ selected: false }, ...r };
          }),
        });
      } catch (e) {
        console.error('Failed to load pay rates');
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin(user) || isUpperManagement(user) || isFinanceUser(user) || isDirector(user)) load();
  }, [employeeId, lastUpdatedAt, filteredOptions]);

  return (
    <>
      <ApprovalChatModal
        isOpen={chatOpen}
        toggle={() => setChatOpen(false)}
        approvalType="pay_raise"
        approvalId={chatPayRateId}
        title={chatTitle}
        schoolId={currentSchool?.value}
      />
      <ModalProfile
        className="modal-dialog-centered"
        isOpen={modalUpdatePayment && !!dataSelected}
        toggle={() => {
          setSelected(null);
          setModalUpdatePayment(!modalUpdatePayment);
        }}
        size={'xs'}
        user={{ employeeId: employeeId || dataSelected?.employeeId || null }}
      >
        <PayRatesForm
          // id={employeeId}
          onSubmit={(_) => {
            setModalUpdatePayment(!modalUpdatePayment);
            setLastUpdatedAt(new Date().getTime());
          }}
          employeeData={dataSelected}
        />
      </ModalProfile>
      <Modal
        className="modal-dialog-centered"
        isOpen={!!dataSelected && !modalUpdatePayment}
        toggle={() => setSelected(null)}
      >
        <div className="modal-header pb-0">
          <h5 className="modal-title" id="Label">
            {`Description`}
          </h5>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => setSelected(null)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body pt-0">
          <PayRatesUpdateDescriptionForm
            employeeId={employeeId || dataSelected?.employeeId}
            dataSelected={dataSelected}
            afterSave={() => setLastUpdatedAt(new Date().getTime())}
          />
        </div>
      </Modal>
      <form>
        <h6 className="heading-small text-muted mb-2">Employees Pay Rates</h6>
        <DataTable
          compact
          columns={PayRatesListColumns({
            maxDescriptionLength: 15,
            setSelected,
            approveAction: handleApprove,
            denyAction,
            viewAction,
            showEmployeeCollumn: !employeeId,
            user,
            chatAction: openPayRateChat,
          })}
          data={tableData.data}
          totalRows={tableData.meta?.total}
          loading={loading}
          title=""
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          conditionalRowStyles={[
            {
              when: (row: any) => !row.deletedAt,
              style: {
                backgroundColor: 'rgb(212, 255, 225)',
              },
            },
          ]}
        />
      </form>
    </>
  );
};

export default PayRatesList;
